import React from 'react';
import { Box, Button, IconButton, Dialog, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AutorenewOutlined,
} from '@mui/icons-material';
import CardCoverImage from './CardCoverImage';

// Shared quick-view modal: an enlarged card cover + a single primary action. Used by the store
// browse rails ("Personalize this card"), the create flow's design options, and the Feeling step's
// example thumbnails ("Use this feeling"). Styling follows the approved mockups
// (docs/mockups/store-quickview-full.html + store-quickview-looping.html): mobile = full-screen
// takeover; desktop = a floating cream card over the dimmed store.
//
// LOOPING (2026-07-10): pass an ordered `items` array + `index` + `onIndexChange(next)` to make it a
// gallery — calm prev/next arrows over the image (wrapping with modulo) + ArrowLeft/ArrowRight keys.
// The single-image props (`cardId`/`imageId`/`coverImageUrl`) still work when no `items` is given.
// CONTEXT-AWARE loop cue: `count={{ index, total }}` shows an honest "i / total" counter (a finite,
// complete set — the Feeling step's 3 samples); `seeAll={{ label, href }}` shows NO count (it's a
// slice of a bigger category — the home rail) and a "See all in {Category}" link instead. The loop
// glyph appears whenever there is more than one item.
const CardQuickViewModal = ({
  open,
  onClose,
  cardId,
  imageId,
  coverImageUrl,
  alt = '',
  actionLabel = 'Personalize this card',
  onConfirm,
  ariaLabel,
  testId = 'card-quickview',
  items,
  index = 0,
  onIndexChange,
  count,
  seeAll,
  // Optional PRIMARY action rendered as the solid button ABOVE, demoting the existing onConfirm/
  // actionLabel to a ghost secondary below (approved mockup: "Create one like this" + "Use this exact
  // card"). Absent → the modal keeps its single-solid-action behavior unchanged.
  primaryAction,
}) => {
  // Cover words (create flow) are BAKED INTO the image server-side — the enlarged cover already
  // contains them, identical to the tile. No CSS overlay here (owner mandate).
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const hasItems = Array.isArray(items) && items.length > 0;
  const total = hasItems ? items.length : 0;
  const safeIndex = hasItems ? ((index % total) + total) % total : 0;
  const active = hasItems ? items[safeIndex] : { cardId, imageId, coverImageUrl };
  const activeAlt = (active && (active.alt || active.name)) || alt;
  const showArrows = hasItems && total > 1;
  const hasCue = showArrows || !!seeAll;

  const go = (direction) => {
    if (!showArrows || typeof onIndexChange !== 'function') return;
    onIndexChange(((safeIndex + direction) % total + total) % total);
  };

  const handleKeyDown = (event) => {
    if (!showArrows) return;
    if (event.key === 'ArrowLeft') {
      event.stopPropagation();
      go(-1);
    } else if (event.key === 'ArrowRight') {
      event.stopPropagation();
      go(1);
    }
  };

  // Subtle teal-tinted close affordance, top-right on both layouts.
  const closeButtonSx = {
    position: 'absolute',
    top: fullScreen ? 14 : 12,
    right: fullScreen ? 14 : 12,
    zIndex: 2,
    width: fullScreen ? 36 : 32,
    height: fullScreen ? 36 : 32,
    color: '#5b6b72',
    backgroundColor: 'rgba(32,104,132,0.10)',
    '&:hover': { backgroundColor: 'rgba(32,104,132,0.18)' },
  };

  // Calm circular prev/next arrows over the enlarged image (per store-quickview-looping.html .nav).
  const arrowSx = (side) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: fullScreen ? 4 : -15,
    zIndex: 3,
    width: 36,
    height: 36,
    color: '#206884',
    backgroundColor: '#FEFDF6',
    border: '0.5px solid #e7e3d6',
    boxShadow: '0 2px 10px rgba(20,28,36,0.22)',
    '&:hover': { backgroundColor: '#fff' },
  });

  // Primary action — teal, full width.
  const actionButtonSx = {
    width: '100%',
    backgroundColor: '#206884',
    color: '#fff',
    boxShadow: 'none',
    fontWeight: 500,
    py: 1.5,
    '&:hover': { backgroundColor: '#185166', boxShadow: 'none' },
  };

  // Ghost/secondary action — teal text, no fill (the demoted onConfirm when a primaryAction is given).
  const ghostButtonSx = {
    width: '100%',
    mt: 1,
    color: '#206884',
    fontWeight: 500,
    py: 1.25,
    textTransform: 'none',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'rgba(32,104,132,0.06)' },
  };

  // The action stack: a primaryAction (solid) demotes the existing onConfirm to a ghost secondary;
  // otherwise the single onConfirm button is the solid primary (unchanged default behavior).
  const renderActions = () =>
    primaryAction ? (
      <>
        <Button
          variant="contained"
          onClick={primaryAction.onClick}
          sx={actionButtonSx}
          data-testid="quickview-primary"
        >
          {primaryAction.label}
        </Button>
        {onConfirm && (
          <Button
            variant="text"
            onClick={onConfirm}
            sx={ghostButtonSx}
            data-testid="quickview-secondary"
          >
            {actionLabel}
          </Button>
        )}
      </>
    ) : (
      <Button variant="contained" onClick={onConfirm} sx={actionButtonSx} data-testid="quickview-primary">
        {actionLabel}
      </Button>
    );

  const renderArrows = () =>
    showArrows ? (
      <>
        <IconButton aria-label="Previous" onClick={() => go(-1)} sx={arrowSx('left')}>
          <ChevronLeftIcon />
        </IconButton>
        <IconButton aria-label="Next" onClick={() => go(1)} sx={arrowSx('right')}>
          <ChevronRightIcon />
        </IconButton>
      </>
    ) : null;

  // Context-aware loop cue beneath the image. Count → honest "i / total" (finite complete set);
  // seeAll → loop glyph + a "See all in {Category}" link (a slice, so no misleading count).
  const renderCue = () => {
    if (!showArrows && !seeAll) return null;
    const counterIndex = count && count.total ? ((count.index % count.total) + count.total) % count.total : 0;
    return (
      <Box
        data-testid="quickview-cue"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, my: 1.25 }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#8a8f8c', fontSize: '0.78rem' }}
        >
          {showArrows && <AutorenewOutlined sx={{ fontSize: 16, color: '#a0a49f' }} />}
          {count && count.total > 1 ? (
            <Box component="span" data-testid="quickview-count">
              {counterIndex + 1} / {count.total}
            </Box>
          ) : (
            showArrows && seeAll && <Box component="span">loops through this rail</Box>
          )}
        </Box>
        {seeAll && (
          <Box
            component={RouterLink}
            to={seeAll.href}
            onClick={onClose}
            data-testid="quickview-seeall"
            sx={{
              color: '#206884',
              fontSize: '0.78rem',
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {seeAll.label} ›
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      fullScreen={fullScreen}
      maxWidth={false}
      data-testid={testId}
      aria-label={ariaLabel}
      BackdropProps={{ sx: { backgroundColor: 'rgba(26,34,44,0.55)' } }}
      PaperProps={{
        sx: fullScreen
          ? {
              m: 0,
              width: '100%',
              maxWidth: '100%',
              backgroundColor: '#FEFDF6',
              display: 'flex',
              flexDirection: 'column',
            }
          : {
              m: 2,
              width: 'auto',
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'calc(100vh - 32px)',
              backgroundColor: '#FEFDF6',
              borderRadius: '14px',
              border: '0.5px solid #e7e3d6',
              boxShadow: '0 18px 54px rgba(20,28,36,0.34)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
      }}
    >
      {fullScreen ? (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: '18px',
              backgroundColor: '#FEFDF6',
            }}
          >
            <IconButton aria-label="Close preview" onClick={onClose} sx={closeButtonSx}>
              <CloseIcon />
            </IconButton>
            <Box sx={{ position: 'relative', width: '94%', maxWidth: '460px' }}>
              <CardCoverImage
                cardId={active.cardId}
                imageId={active.imageId}
                coverImageUrl={active.coverImageUrl}
                alt={activeAlt}
                sx={{
                  width: '100%',
                  height: 'auto',
                  aspectRatio: '5 / 7',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  boxShadow: '0 10px 26px rgba(20,28,36,0.2)',
                }}
              />
              {renderArrows()}
            </Box>
          </Box>
          <Box
            sx={{
              p: '14px 18px calc(14px + env(safe-area-inset-bottom))',
              borderTop: '1px solid #efe9da',
              backgroundColor: '#FEFDF6',
            }}
          >
            {renderCue()}
            {renderActions()}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            position: 'relative',
            p: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <IconButton aria-label="Close preview" onClick={onClose} sx={closeButtonSx}>
            <CloseIcon />
          </IconButton>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <CardCoverImage
              cardId={active.cardId}
              imageId={active.imageId}
              coverImageUrl={active.coverImageUrl}
              alt={activeAlt}
              sx={{
                width: 'auto',
                height: 'min(64vh, 620px)',
                maxWidth: '100%',
                aspectRatio: '5 / 7',
                objectFit: 'cover',
                borderRadius: '9px',
                boxShadow: '0 8px 24px rgba(20,28,36,0.18)',
              }}
            />
            {renderArrows()}
          </Box>
          {renderCue()}
          <Box sx={{ width: '100%', mt: hasCue ? 0 : 2 }}>{renderActions()}</Box>
        </Box>
      )}
    </Dialog>
  );
};

export default CardQuickViewModal;
